const en = {
  language: {
    label: 'Language',
    polish: 'Polski',
    english: 'English',
    description: 'A polished configuration area for the future product. Some controls still behave as working placeholders.',
  },
  common: {
    buttons: {
      openAssets: 'Open assets',
      filters: 'Filters',
      retry: 'Try again',
      addDemoAsset: 'Add asset',
      view: 'View',
      close: 'Close',
      keepExploring: 'Keep exploring',
    allowInvites: 'Allow invitations',
      exportSample: 'Export sample',
      createReportSchedule: 'Create report schedule',
      saveChanges: 'Save changes',
      signIn: 'Sign in',
      exploreUi: 'Explore UI',
    },
    status: {
      liveFeed: 'Live feed',
      operationalActions: 'Operational actions',
      productionPreview: 'Production preview',
      completed: 'Completed',
      needsReview: 'Needs review',
      out: 'Out',
      available: 'Available',
      inRepair: 'In repair',
      reserved: 'Reserved',
      retired: 'Retired',
      active: 'Active',
      pendingReview: 'Pending review',
      closed: 'Closed',
    versionValue: 'Production build',
      operational: 'Operational',
      limitedAccess: 'Limited access',
    },
  },
  navigation: {
    title: 'Navigation',
    dashboard: 'Dashboard',
    assets: 'Assets',
    scan: 'Scanning',
      notifications: 'Notification controls will connect to the backend in a later step.',
    inventory: 'Inventory',
    createDialog: {
      title: 'Add a Varibox to the database',
      description: 'Fill in the required fields to create a new record through the API.',
      serialNumber: 'Serial number',
      radexIdentifier: 'RADEX number',
      barcode: 'Barcode',
      capacity: 'Capacity [L]',
      color: 'Color',
      sealType: 'Seal type',
      status: 'Status',
      contract: 'Contract',
      client: 'Client',
      clientPlaceholder: 'Choose a client if the record should be assigned',
      noClients: 'No clients available',
      noClient: 'Unassigned',
      rfidTag: 'RFID tag',
      rfidTagHelp: 'Optional field for the RFID tag.',
      notes: 'Notes',
      save: 'Save to database',
      saving: 'Saving...',
      checkingDuplicates: 'Checking identifiers...',
      duplicateWarning: 'At least one identifier is already assigned to another Varibox.',
      duplicateCheckError: 'Unable to check identifiers. The server will still validate them when saving.',
      duplicateSerial: 'This serial number is already in use.',
      duplicateRadex: 'This RADEX number is already in use.',
      duplicateBarcode: 'This barcode is already in use.',
      error: 'Unable to create the Varibox.',
    },
    reports: 'Reports',
    users: 'Users',
    settings: 'Settings',
    workspace: 'Workspace',
    workspaceDescription: 'Operational UI for Varibox Control Center, ready for API work.',
    productionReady: 'Production ready',
    loginScreen: 'Login screen',
    appTitle: 'Varibox Control Center',
    appSubtitle: 'Desktop-first control room for Varibox operations',
    searchPlaceholder: 'Search assets, users, or locations',
    productionMode: 'Production mode',
    alerts: 'Alerts',
    userRole: 'Operations Admin',
    workspaceLabel: 'Varibox Control Center',
  },
  login: {
    eyebrow: 'Access preview',
    platformLabel: 'OPERATIONS PLATFORM',
    productTitle: 'Varibox Control Center',
    byline: 'by myZenor',
    productDescription: 'One place to scan Variboxes, manage locations, and oversee warehouse operations.',
    scanFeatureTitle: 'Scanning',
    scanFeatureDescription: 'Quick scanning with a phone camera or Zebra reader.',
    warehouseFeatureTitle: 'Warehouses',
    warehouseFeatureDescription: 'Assign each Varibox to the right PL01 or PL99 subwarehouse.',
    securityFeatureTitle: 'Access and security',
    securityFeatureDescription: 'Personal accounts with features tailored to each user role.',
    secureAccess: 'Secure access to your organization’s resources and data.',
    welcomeBack: 'WELCOME BACK',
    signInTitle: 'Sign in',
    signInDescription: 'Enter the username and password provided by your administrator.',
    loginLabel: 'Username',
    passwordLabel: 'Password',
    submit: 'Sign in',
    submitting: 'Signing in…',
    submitError: 'Unable to sign in. Please try again.',
    noAccount: 'Don’t have an account?',
    noAccountDescription: 'Ask your administrator to create an account and grant you access.',
    backHome: 'Home',
    footerBrand: 'Varibox Control Center',
    title: 'Sign in to the platform',
    description: 'The form acts as the entry point to the dashboard and can later be wired to real authentication.',
    info: 'Authentication is not wired to the backend yet.',
    readyNow: 'What is ready now',
    readyNowDescription: 'This UI includes navigation, dashboard cards, table views, settings sections, and operational report placeholders.',
    heroTitle: 'Varibox Control Center presented as a polished desktop workspace.',
    heroDescription: 'The screen keeps a layout that can later plug into real authentication without changing the UI.',
    snapshot: 'Live platform snapshot',
    accessMode: 'Access mode',
    noAuth: 'backend auth will be wired in a later step',
    enterpriseVisibility: 'Enterprise visibility',
    auditWorkflows: 'Audit-ready workflows',
    roleAwareUi: 'Role-aware UI',
    emailLabel: 'Email',
    readyState: 'Ready',
  },
  dashboard: {
    eyebrow: 'Dashboard',
    title: 'Operations at a glance',
    description: 'An operational command center for assets, locations, inventory sessions, and live movement activity. The view is ready for API-driven data.',
    recentMovements: 'Recent movements',
    recentMovementsDescription: 'The latest asset transfers and status changes from the current operational stream.',
    quickActions: 'Quick actions',
    quickActionsDescription: 'Common operational shortcuts for the control room.',
    loadingPreview: 'Loading state preview',
    loadingPreviewDescription: 'Skeletons and progress indicators for future live API fetching.',
    loadingData: 'Loading dashboard data...',
    apiError: 'Unable to load dashboard data from API.',
    lastSync: 'Last sync 2 min ago',
    liveFeed: 'Live feed',
    totalAssets: 'Total Assets',
    availableAssets: 'Available Assets',
    assetsInRepair: 'Assets in Repair',
    assetsOut: 'Assets Out',
    quickActionLabels: {
      registerAssetBatch: 'Register asset batch',
      scheduleInventoryRun: 'Schedule inventory run',
      reviewExceptionReport: 'Review exception report',
      inviteUser: 'Invite a new user',
    },
    quickActionDescriptions: {
      registerAssetBatch: 'Prepare incoming stock for intake review.',
      scheduleInventoryRun: 'Open a new count session for a warehouse.',
      reviewExceptionReport: 'Inspect damaged, missing, or overdue items.',
      inviteUser: 'Add a manager, operator, or viewer to the workspace.',
    },
    metricHelpers: {
      totalAssets: 'Across 18 active locations',
      availableAssets: 'Ready for allocation',
      assetsInRepair: 'Awaiting maintenance closeout',
      assetsOut: 'Checked out to users and sites',
    },
    movementDescriptions: {
      completed: 'Transferred from Warsaw Hub to Dock 3',
      needsReview: 'Marked for repair inspection',
      out: 'Checked out to Project Orion',
      completedReturn: 'Returned to central storage',
    },
    movementTimestamps: {
      min12: '12 min ago',
      min34: '34 min ago',
      hour1: '1 hour ago',
      hour2: '2 hours ago',
    },
  },
  assets: {
    eyebrow: 'Assets / Variboxes',
    title: 'Assets / Variboxes',
    description: 'An operational screen for managing reusable Varibox containers with search, filters, stats, and history preview.',
    searchLabel: 'Search Variboxes',
    searchPlaceholder: 'Serial, RADEX, barcode, RFID, client, or location',
    searchHint: 'Enter multiple words; they can match different fields on the same Varibox.',
    buttons: {
      addVaribox: 'Add Varibox',
      scanVaribox: 'Scan Varibox',
      editSelected: 'Edit selected',
      export: 'Export',
      viewHistory: 'View history',
      clearFilters: 'Clear filters',
      clearSearch: 'Clear search',
      clientBalance: 'Client balance / PDF',
    },
    preview: 'Varibox preview',
    stats: {
      all: 'All',
      available: 'Available',
      occupied: 'Occupied',
      repair: 'In repair',
      blocked: 'Blocked',
    },
    menu: {
      details: 'Details',
      history: 'History',
      edit: 'Edit',
      block: 'Block',
        delete: 'Delete',
    },
    filters: {
      status: 'Status',
      client: 'Client',
      capacity: 'Capacity',
      sealType: 'Seal type',
      contract: 'Contract',
      location: 'Location',
      locationFromMap: 'Client map view',
      all: 'All',
      yes: 'Yes',
      no: 'No',
    },
    emptyState: {
      title: 'No results for the selected filters',
      description: 'Change the search criteria or clear filters to see the available Variboxes.',
    },
    loadingData: 'Loading Varibox data...',
    loadingDetails: 'Loading Varibox details...',
    apiError: 'Unable to load Varibox data from API.',
      delete: {
        confirm: 'Are you sure you want to delete Varibox',
        success: 'Varibox deleted',
        error: 'Failed to delete Varibox.',
        bulkButton: 'Delete selected ({count})',
        bulkConfirm: 'Are you sure you want to delete selected Variboxes? Count: {count}',
        bulkSuccess: 'Deleted Variboxes: {count}',
        bulkError: 'Failed to delete selected Variboxes.',
        selectAll: 'Select all visible Variboxes',
        selectOne: 'Select Varibox for deletion',
      },
    detailsApiError: 'Unable to load Varibox details.',
    historyUnavailable: 'Movement history is not available from API yet.',
    quickStatsLabels: {
      all: 'All',
      available: 'Available',
      occupied: 'Occupied',
      repair: 'In repair',
      blocked: 'Blocked',
    },
    headings: {
      serialNumber: 'Serial number',
      radexId: 'RADEX identification number',
      capacity: 'Capacity [L]',
      bodyColor: 'Body color',
      sealType: 'Seal type',
      status: 'Status',
      client: 'Client',
      contract: 'Contract',
      rfid: 'RFID',
      actions: 'Actions',
    },
    statuses: {
      available: 'available',
      occupied: 'occupied',
      damaged: 'damaged',
      repair: 'repair',
      cleaning: 'cleaning',
      blocked: 'blocked',
    },
    contractValues: {
      tak: 'yes',
      nie: 'no',
    },
    variboxDetails: {
      title: 'Varibox details',
      editData: 'Edit Varibox data',
      capacity: 'Capacity',
      color: 'Color',
      seal: 'Seal',
      client: 'Client',
      contract: 'Contract',
      lastLocation: 'Last location',
      lastMovement: 'Last movement',
      qrTitle: 'Scannable QR code',
      qrDescription: 'The code contains the Varibox identifier and technical details. Scanning opens the latest data from the system.',
      qrGenerate: 'Generate QR code',
      qrHide: 'Hide QR code',
      qrPrint: 'Print label',
      qrPopupBlocked: 'The browser blocked the print window. Allow pop-ups and try again.',
      historyTitle: 'Varibox history',
      historyDescription: 'Realistic mock of movements, cleaning, repairs, and dispatches.',
      adminTitle: 'Admin panel',
      adminDescription: 'From details view you can activate the Varibox and assign a warehouse location.',
      locationLabel: 'Location',
      locationPlaceholder: 'Select location',
      rootLocationLabel: 'Root warehouse',
      rootLocationPlaceholder: 'Choose PL01 or PL99',
      sublocationLabel: 'Subwarehouse / sublocation',
      sublocationSelectRootHint: 'Select a root warehouse first to see subwarehouses.',
      sublocationNoOptionsHint: 'No subwarehouses are defined yet for the selected root warehouse.',
      sublocationKeepRoot: 'Keep in root warehouse',
      activateAndSave: 'Activate and save',
      saveLocation: 'Save location',
      adminSaved: 'Varibox changes were saved.',
      adminSaveError: 'Unable to save Varibox changes.',
      adminNoChanges: 'No changes to save.',
    },
    createDialog: {
      title: 'Add a Varibox to the database',
      description: 'Fill in the required fields to create a new record through the API.',
      serialNumber: 'Serial number',
      radexIdentifier: 'RADEX number',
      barcode: 'Barcode',
      capacity: 'Capacity [L]',
      color: 'Color',
      sealType: 'Seal type',
      status: 'Status',
      contract: 'Contract',
      client: 'Client',
      clientPlaceholder: 'Choose a client if the record should be assigned',
      noClients: 'No clients available',
      noClient: 'Unassigned',
      rfidTag: 'RFID tag',
      rfidTagHelp: 'Optional field for the RFID tag.',
      notes: 'Notes',
      save: 'Save to database',
      saving: 'Saving...',
      error: 'Unable to create the Varibox.',
    },
    editDialog: {
      title: 'Edit Varibox data',
      description: 'Update the container details. Changes are saved to the system when confirmed.',
      loadError: 'Unable to load Varibox data for editing.',
      error: 'Unable to save Varibox changes.',
      duplicateSerial: 'This serial number is already assigned to another Varibox.',
      duplicateRadex: 'This RADEX number is already assigned to another Varibox.',
      duplicateBarcode: 'This barcode is already assigned to another Varibox.',
      duplicateWarning: 'At least one identifier is already assigned to another Varibox.',
      checkingDuplicates: 'Checking identifiers...',
      duplicateCheckError: 'Unable to check identifiers. The server will still validate them when saving.',
      location: 'Location',
      keepLocation: 'Keep current location',
      save: 'Save changes',
      saving: 'Saving...',
    },
  },
  scan: {
    eyebrow: 'Scanning',
    title: 'Scan Varibox',
    description: 'A fast barcode screen for keyboard scanners, Zebra devices, and a phone camera.',
    mobile: {
      title: 'Scan a Varibox',
      description: 'Tap the button and point your camera at the code. The result appears right away.',
      cameraButton: 'Open camera scanner',
      manualButton: 'Enter code manually',
      nextScan: 'Scan next Varibox',
    },
    scanNowTitle: 'Ready to scan',
    scanNowDescription: 'Scan a barcode and press Enter. Zebra behaves like a keyboard.',
    helper: 'The input stays focused so you can scan or type a code immediately.',
    barcodeLabel: 'Barcode',
    barcodePlaceholder: 'Scan or type the Varibox serial number',
    keyboardHint: 'A Zebra scanner can work as a keyboard. Press Enter after the barcode.',
    lastBarcodeLabel: 'Last scanned barcode',
    waitingState: 'Waiting for a scan',
    waitingHint: 'The input stays focused so you can scan or type a code immediately.',
    scanningMessage: 'Scanning...',
    foundMessage: 'Varibox found',
    notFoundMessage: 'Varibox not found',
    errorMessage: 'An error occurred during scanning',
    unknownTitle: 'Varibox not found',
    unknownDescription: 'The code does not exist in the database. Try again or enter it manually. Only an administrator can add a new Varibox.',
    foundPanelTitle: 'Scan result',
    foundPanelDescription: 'When a barcode matches, we show a full preview of the selected Varibox.',
    quickActionsTitle: 'Quick actions',
    historyTitle: 'Recent scans',
    historyDescription: 'The current scanning session history is stored only in frontend state.',
    historyEmpty: 'No scans in the current session yet.',
    notFoundHistoryValue: 'Not found',
    buttons: {
      scan: 'Scan',
      manual: 'Enter manually',
      camera: 'Use camera',
      cancel: 'Cancel / Back',
      retry: 'Try again',
      nextScan: 'Scan next',
      viewMovementHistory: 'View history',
      addVaribox: 'Add Varibox',
      details: 'Details',
      history: 'History',
      issue: 'Issue',
      receive: 'Receive',
      block: 'Block',
    },
    status: {
      ready: 'Ready to scan',
      scanning: 'Scanning...',
      found: 'Found',
      notFound: 'Not found',
      error: 'Error',
    },
    historyResults: {
      found: 'Found',
      notFound: 'Not found',
      error: 'Error',
    },
    availability: {
      enabled: 'Operationally available',
      disabled: 'Disabled from operation',
    },
    locations: {
      PL01: 'Operational hub',
      PL99: 'Client locations',
    },
    sublocations: {
      magazyn: 'Warehouse',
      naprawa: 'Repair',
      mycie: 'Cleaning',
      uszkodzone: 'Damaged',
      cedrob: 'Cedrob',
      grot: 'Grot',
      prosziaczek: 'Prosziaczek',
    },
    movement: {
      currentLocation: 'Current location',
      destinationLocation: 'Destination location',
      destinationOption: 'Sublocation / client',
      selectDestination: 'Select destination location',
      selectDestinationOption: 'Select sublocation or client',
      suggestedDestination: 'Suggested return destination',
      note: 'Note (optional)',
      notePlaceholder: 'Add a movement note (for example: returned after cleaning)',
      addPhoto: 'Add photo (optional)',
      removePhoto: 'Remove photo',
      photoHint: 'The image is stored in local frontend working state only and is not uploaded to backend.',
      adminCorrectionHint: 'Movements can be corrected later by an Administrator. This stage is working workflow only.',
      successTitle: 'Movement recorded',
      successDescription: 'The movement was recorded immediately after destination selection.',
      previousLocation: 'Previous location',
      dateTime: 'Date and time',
      availability: 'Availability',
      user: 'User',
      photo: 'Photo preview',
      historyTitle: 'Movement history',
      historyDescription: 'Recent moves stored in local working state.',
      historyEmpty: 'No saved movements in this session.',
    },
    assign: {
      title: 'Assign after scan',
      description: 'After scanning, choose root warehouse and subwarehouse, then save the Varibox location.',
      chooseSubwarehouse: 'Choose a subwarehouse',
      saveAndNext: 'Save location',
      saving: 'Saving location…',
      saved: 'Varibox location has been saved.',
      noChanges: 'No location changes detected.',
      error: 'Unable to save location.',
    },
    workflowActions: {
      camera: 'The phone camera has been opened.',
      add: 'Adding a Varibox opens the database form.',
      issue: 'Varibox issue handling will be wired into the workflow.',
      receive: 'Varibox receiving will be wired into the workflow.',
      block: 'Varibox blocking will be wired into the workflow.',
    },
    camera: {
      title: 'Scan with phone camera',
      description: 'Point the camera at a barcode. The app will try to read it automatically.',
      modeLabel: 'Choose the code type to scan',
      barcodeMode: 'Barcode',
      qrMode: 'QR code',
      barcodeDescription: 'This mode reads 1D barcodes and ignores QR codes. Point the camera at the barcode below the serial number.',
      qrDescription: 'This mode reads QR codes only.',
      loading: 'Starting camera...',
      tip: 'If your browser does not support camera scanning, use manual entry or a Zebra scanner.',
      unavailable: 'Your browser does not support camera scanning.',
      close: 'Close',
      restart: 'Restart',
    },
  },
  locations: {
    eyebrow: 'Locations',
    title: 'Warehouses and operational sites',
    description: 'A visual map of the Varibox Control Center network. Each location card shows occupancy, supervisor ownership, and the current operating status.',
    treeTitle: 'Varibox location tree',
    treeDescription: 'Fast overview of where Varibox containers are located, with counts and container list for the selected node.',
    treeSectionTitle: 'Location structure',
    countSuffix: 'Variboxes',
    selectHint: 'Select a location or sublocation from the tree to view containers.',
    emptyNode: 'No Variboxes in the selected location.',
    loadingData: 'Loading location tree...',
    apiError: 'Unable to load locations from API.',
    lastMovement: 'Last movement',
    actions: {
      scanVaribox: 'Scan Varibox',
      searchVaribox: 'Search Varibox',
      inventory: 'Inventory',
    },
    overview: {
      total: 'Total',
      available: 'Available',
      occupied: 'Occupied',
      repair: 'Repair',
      blocked: 'Blocked',
    },
    mobile: {
      title: 'Varibox locations',
      description: 'Mobile location browser with scanning as the primary action.',
      variboxCount: 'Variboxes',
      openLocation: 'Open',
      openList: 'Show list',
      backToRoots: 'Back to root locations',
      recentScans: 'Recent scans',
      noRecentScans: 'No recent scans.',
    },
    supervisor: 'Supervisor',
    occupancy: 'Occupancy',
    capacityUsed: 'capacity used',
    demoRecord: 'Location record',
    categories: {
      primaryWarehouse: 'Primary warehouse',
      regionalDepot: 'Regional depot',
      repairInspection: 'Repair and inspection',
      overflowHub: 'Overflow hub',
    },
    notes: {
      mainDispatch: 'Main dispatch and return hub for daily operations.',
      coastalShipments: 'Supports coastal shipments and quick turnaround requests.',
      maintenanceArea: 'Dedicated maintenance area for damaged or overdue assets.',
      seasonalBuffer: 'Flexible buffer for seasonal demand spikes and new intake.',
    },
    adminPanel: {
      title: 'Subwarehouse panel',
      description: 'Add a subwarehouse or client node under a selected root warehouse (for example PL01 or PL99).',
      parentLabel: 'Parent warehouse',
      kindLabel: 'Node type',
      kindSublocation: 'Subwarehouse',
      kindClientNode: 'Client node',
      nameLabel: 'Subwarehouse / company name',
      codeLabel: 'Code (optional)',
      addressLabel: 'Company address',
      postalCodeLabel: 'Postal code',
      cityLabel: 'City',
      cityCodeLabel: 'City code (optional)',
      companyProfileHint: 'For PL01 or PL99 branches, add company and address details to show a marker on the Poland map.',
      companyProfileOnlyHint: 'Company and address details are available in PL01 and PL99 branches.',
      companyProfileRequiredHint: 'For PL01 or PL99, provide address, postal code, and city.',
      createButton: 'Add subwarehouse',
      creating: 'Adding...',
      created: 'Subwarehouse was added.',
      createError: 'Unable to add subwarehouse.',
    },
    map: {
      title: 'Company map — PL01 and PL99',
      description: 'Company locations assigned to PL01 and PL99 branches on a Poland map.',
      loading: 'Loading map points...',
      empty: 'No company points to display on the map.',
      selectedClientCount: 'Client Varibox count',
      goToClientState: 'Open client state',
      markerSublocation: 'Subwarehouse',
      markerClientNode: 'Client node',
    },
  },
  inventory: {
    eyebrow: 'Inventory',
    sessionTitle: 'Location Inventory Session',
    sessionDescription: 'Frontend-only scanning workflow for a selected location: expected, unexpected, missing, completion summary, and session history.',
    title: 'Inventory sessions',
    description: 'A session-oriented view for stock counts, exception handling, and session status. This is a visual mock of the future inventory workflow.',
    startTitle: 'Start an inventory session',
    activeSessionTitle: 'Active scanning session',
    historyTitle: 'Inventory session history',
    apiError: 'Unable to load locations and Variboxes for inventory.',
    noLocationSelected: 'No location selected',
    locationCodeLabel: 'Location code',
    locationOptionLabel: 'Sublocation / client',
    selectLocationCode: 'Select location code',
    selectLocationOption: 'Select sublocation or client',
    rootLocationFallback: 'This location has no sublocations — inventory will cover the selected root location.',
    locationLabel: 'Inventory location',
    currentLocationLabel: 'Current container location',
    inventoryLocationLabel: 'Inventory session location',
    scannerTitle: 'Barcode scanner',
    camera: {
      title: 'Scan with camera',
      modeLabel: 'Choose the code type to scan',
      barcodeMode: 'Barcode',
      qrMode: 'QR code',
      barcodeDescription: 'Reads 1D barcodes and ignores QR codes.',
      qrDescription: 'Reads QR codes only.',
      loading: 'Starting camera...',
      unavailable: 'Unable to start the camera. Check browser permissions.',
      close: 'Close',
    },
    barcodeLabel: 'Barcode',
    barcodePlaceholder: 'Scan with Zebra or enter barcode manually',
    counters: {
      expected: 'Expected',
      scanned: 'Scanned',
      found: 'Found',
      unexpected: 'Unexpected',
      missing: 'Missing',
    },
    results: {
      foundTitle: 'Matched inventory location',
      unexpectedTitle: 'Container in different location',
      unexpectedDescription: 'Container was scanned, but is currently in another location.',
      duplicate: 'Duplicate scan',
      unknown: 'Unknown barcode',
    },
    summary: {
      title: 'Completed session summary',
      foundList: 'Found list',
      unexpectedList: 'Unexpected list',
      missingList: 'Missing list',
    },
    history: {
      location: 'Location',
      date: 'Completed date',
      progress: 'Result',
      missing: 'Missing',
      status: 'Status',
      missingSuffix: 'miss.',
    },
    historyEmpty: 'No completed inventory sessions yet.',
    sessionStatus: {
      completed: 'Completed',
    },
    buttons: {
      startScanning: 'Start scanning',
      scanNext: 'Scan next',
      scanVariboxMobile: 'Scan Varibox',
      scanWithCamera: 'Scan with camera',
      finishInventory: 'Finish inventory',
      newSession: 'New session',
      closeSession: 'Close',
    },
    activeSession: 'Active session',
    itemsCounted: 'Items counted',
    exceptions: 'Exceptions',
    sessionProgress: 'Session progress',
    workflowPreview: 'Inventory workflow preview',
    countCollection: 'Count collection',
    countCollectionDescription: 'Operators scan and validate asset barcodes.',
    exceptionReview: 'Exception review',
    exceptionReviewDescription: 'Missing or overcounted items are marked for follow-up.',
    sessionCloseout: 'Session closeout',
    sessionCloseoutDescription: 'Reports are prepared for approval and archive.',
    sessionItems: 'items',
    sessionNames: {
      augustAudit: 'August warehouse audit',
      coastalCount: 'Coastal depot cycle count',
      repairVerification: 'Repair bay verification',
    },
    sessionStatusLabels: {
      inProgress: 'In progress',
      pendingReview: 'Pending review',
      closed: 'Closed',
    },
    sessionStartedAt: {
      today0630: 'Started today at 06:30',
      yesterday1815: 'Completed yesterday at 18:15',
      aug07: 'Closed on Aug 07',
    },
  },
  clientBalance: {
    eyebrow: 'Client card',
    title: 'Varibox balance',
    description: 'Formal statement of Variboxes assigned to the recipient, including company details, address, and serial numbers.',
    loadError: 'Unable to load the client balance.',
    backToAssets: 'Back to assets',
    exportPdf: 'Save as PDF',
    generatedAt: 'Balance as of',
    recipient: 'Recipient details',
    statementLabel: 'Reusable containers entrusted to recipient',
    statementTitle: 'Request to return / reconcile the balance',
    reference: 'Location / recipient',
    statementDate: 'Statement date',
    returnRequestTitle: 'Please return containers or reconcile the balance',
    returnRequest: 'According to our records, the Varibox containers listed below remain entrusted to the recipient. Please return the listed containers promptly or contact us to reconcile the balance and resolve any discrepancies. Please refer to the serial numbers in this statement when arranging a return.',
    currentStock: 'Variboxes in stock',
    variboxUnit: 'currently assigned to this client',
    recordedDays: 'Recorded stay days',
    recordedDaysNote: 'Sum of current stays with available history',
    missingHistory: 'Without start date',
    missingHistoryNote: 'No recorded entry into a client location',
    detailsTitle: 'Container balance details',
    detailsDescription: 'Current Variboxes assigned to the client.',
    serialNumber: 'Serial number',
    status: 'Status',
    location: 'Location',
    since: 'With client since',
    days: 'Days',
    noHistory: 'No history',
    empty: 'No Variboxes are assigned to this client.',
    calculationNote: 'This statement reflects current assignments in our records and is intended to reconcile the balance. Stay duration is calculated only from recorded movements; start dates may be unavailable for older assignments.',
  },
  reports: {
    eyebrow: 'Reports',
    title: 'Operational reporting',
    description: 'See how often each Varibox changes location, analyze movement trends, and download a detailed Excel report.',
    period: 'Report period',
    fromDate: 'From date',
    toDate: 'To date',
    totalMoves: 'Total movements',
    variboxesMoved: 'Variboxes moved',
    averageMoves: 'Average moves per Varibox',
    movementTrendTitle: 'Movements over time',
    movementTrendDescription: 'Number of location changes during the selected period.',
    mostMovedTitle: 'Most frequently moved Variboxes',
    mostMovedDescription: 'Ranked by recorded location changes.',
    searchVaribox: 'Find Varibox by serial number or RADEX',
    noMatchingVaribox: 'No movement found for a matching Varibox.',
    latestMovementsTitle: 'Recent movements',
    latestMovementsDescription: 'Movement history including source and destination locations.',
    serialNumber: 'Serial number',
    radex: 'RADEX',
    moveCount: 'Movement count',
    dateTime: 'Date and time',
    route: 'Route',
    actor: 'Moved by',
    exportExcel: 'Download Excel',
    exporting: 'Preparing…',
    exportSuccess: 'Excel report downloaded.',
    exportError: 'Unable to generate the Excel file.',
    loadError: 'Unable to load report data.',
    noMovementData: 'No recorded movements in this period.',
    historyStartsNotice: 'Movement history is recorded from this feature’s deployment; earlier transfers cannot be reconstructed.',
    printSummary: 'Print summary',
    exportSample: 'Export sample',
    reportDelivery: 'Report delivery pipeline',
    reportDeliveryDescription: 'Visual placeholder for scheduled exports, email distribution, and approval steps.',
    scheduledExports: 'Scheduled exports',
    approvedReports: 'Approved reports',
    emptyState: 'Empty-state preview',
    emptyStateDescription: 'Some report types have no saved schedules yet. The UI should still feel complete.',
    noReports: 'No custom reports configured',
    emptyStateHint: 'Add a schedule later to generate automated output for your team.',
    createReportSchedule: 'Create report schedule',
    cards: {
      inventoryBalance: {
        title: 'Inventory balance',
        description: 'Current stock, losses, and adjustments by location.',
        accent: 'Primary health indicator',
        actionLabel: 'Open sample report',
      },
      movementTrend: {
        title: 'Movement trend',
        description: 'Transfers, check-ins, and check-outs across the week.',
        accent: 'Operations trend',
        actionLabel: 'Inspect movement log',
      },
      repairBacklog: {
        title: 'Repair backlog',
        description: 'Open maintenance cases and estimated closeout window.',
        accent: 'Service queue',
        actionLabel: 'Review maintenance queue',
      },
    },
  },
  users: {
    eyebrow: 'Users',
    title: 'Role and access overview',
    description: 'Example user records with role badges, team ownership, and a visual access status.',
    totalUsers: 'Total users',
    activeAdmins: 'Active admins',
    viewerAccounts: 'Viewer accounts',
    headings: {
      user: 'User',
      email: 'Email',
      role: 'Role',
      team: 'Team',
      status: 'Status',
      lastSeen: 'Last seen',
    },
    roles: {
      admin: 'Admin',
      manager: 'Manager',
      user: 'User',
      viewer: 'Viewer',
    },
    summaries: {
      totalUsers: 'Mock roster prepared for future role-based permissions.',
      activeAdmins: 'Workspace administration and policy control.',
      viewerAccounts: 'Read-only access for auditing and oversight.',
    },
  },
  settings: {
    eyebrow: 'Settings',
    title: 'Workspace settings',
    description: 'A polished configuration surface for the future product. The controls are visual placeholders and are not persisted yet.',
    saveDraft: 'Save visual draft',
    info: 'No backend settings workflow exists yet. This page is intentionally mock-only while preserving a production-ready structure.',
    general: 'General',
    language: 'Language',
    appearance: 'Appearance',
    notifications: 'Notifications',
    applicationInfo: 'Application information',
    workspaceName: 'Workspace name',
    timezone: 'Timezone',
    allowInvites: 'Allow invitations',
    requireApproval: 'Require manager approval for access changes',
    sessionTimeoutWarnings: 'Session timeout warnings',
    maintenanceReminders: 'Maintenance reminders',
    transferNotifications: 'Transfer notifications',
    executiveDigest: 'Executive digest',
    retentionPeriod: 'Retention period',
    exportWindow: 'Export window',
    saveSettings: 'Save settings',
    currentLanguage: 'Current language',
    languageDescription: 'The selected language is saved locally and remains after browser refresh.',
    appearanceDescription: 'We keep the current Material UI style and improve it without redesigning the interface.',
    generalDescription: 'Core workspace settings and defaults.',
    notificationsDescription: 'Configure which events appear in the preview.',
    appInfoDescription: 'Production build version and environment details.',
    appName: 'Varibox Control Center',
    appVersion: 'Version',
    architecture: 'Architecture',
    architectureValue: 'V3 frozen',
    stack: 'Stack',
    stackValue: 'React + TypeScript + Material UI',
    versionValue: 'Production build',
    appearanceOptions: {
      compactTableDensity: 'Compact table density',
      stickyHeader: 'Sticky header',
      highContrastAccents: 'High contrast accents',
    },
    helperTexts: {
      timezone: 'UTC, Europe/Warsaw, Europe/London',
      currentLanguage: 'The choice is stored in localStorage and applied immediately.',
      appearance: 'The current Material UI style remains intact.',
      notifications: 'Notification controls will connect to the backend in a later step.',
    },
    import: {
      title: 'Varibox Excel import',
      description: 'Upload a .xlsx file, run validation, and then import records into the database.',
      chooseFile: 'Choose .xlsx file',
      validateButton: 'Validate file',
      importButton: 'Import data',
      validating: 'Validating...',
      importing: 'Importing...',
      validationPassed: 'Validation passed. You can run the import.',
      validationFailed: 'Validation found errors. Import will not run.',
      reportTitle: 'Validation report',
      totalRows: 'Total rows',
      importableRows: 'Rows ready to import',
      imported: 'Imported',
      skippedExisting: 'Skipped (already exists)',
      errors: 'Errors',
      row: 'Row',
      clientsCreated: 'Clients created',
      clientsReused: 'Existing clients reused',
      pl01Count: 'Assigned to PL01',
      pl99Count: 'Assigned to PL99',
      importFinished: 'Import completed.',
      uploadFailed: 'Failed to upload or process the file.',
    },
  },

  loginDetails: {
    team: 'Team',
  },
} as const;

export default en;